import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Shuffle, Search, AlertTriangle } from "lucide-react";
import emojiData from "@/data/emoji_data.json";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ScrollArea } from "@/components/ui/scroll-area";

interface SelectedEmoji {
  char: string;
  code: string;
}

interface EmojiData {
  unicode_code_point: string;
  emoji_char: string;
  name: string;
  category: string;
  subcategory: string;
  keywords: string[];
  emoji_version: string;
}

interface EmojiDataFile {
  emojis: EmojiData[];
  metadata: {
    total_count: number;
    generated_at: string;
    unicode_version: string;
  };
}

// Import the emoji data with proper typing
const emojiJsonData = emojiData as EmojiDataFile;

// Get unique emojis from the emoji data
const UNIQUE_EMOJIS: SelectedEmoji[] = emojiJsonData.emojis.map(emoji => ({
  char: emoji.emoji_char,
  code: emoji.unicode_code_point
}));

// Get unique categories from emoji data
const CATEGORIES = Array.from(
  new Set(emojiJsonData.emojis.map(emoji => emoji.category))
).sort();

const COMPAT_OPTIONS = [
  { value: "14.0", label: "Broadly Supported" },
  { value: "15.1", label: "Recent Devices" },
  { value: "all", label: "All Versions" },
];

interface EmojiCustomizerProps {
  onSave: (emojis: SelectedEmoji[], name?: string, id?: string) => void;
  onCancel: () => void;
  onSelectionChange?: (emojis: SelectedEmoji[]) => void;
  initialEmojis?: SelectedEmoji[];
  initialName?: string;
  initialId?: string;
  parentName?: string;
  requiredCount: number;
}

const DEFAULT_EMOJIS: SelectedEmoji[] = [
  { char: "😀", code: "U+1F600" },
  { char: "😂", code: "U+1F602" },
  { char: "🤣", code: "U+1F923" },
  { char: "😊", code: "U+1F60A" },
  { char: "😍", code: "U+1F60D" }
];

export default function EmojiCustomizer({
  onSave,
  onCancel,
  onSelectionChange,
  initialEmojis = DEFAULT_EMOJIS,
  initialName = "",
  initialId,
  parentName,
  requiredCount,
}: EmojiCustomizerProps) {
  // Use initialEmojis directly from props - this is now a controlled component
  const selectedEmojis = initialEmojis;
  
  const [searchTerm, setSearchTerm] = useState("");
  const [currentCategory, setCurrentCategory] = useState("all");
  const [compatVersion, setCompatVersion] = useState("14.0");
  const [emojiSetName, setEmojiSetName] = useState(
    initialName || (parentName ? `${parentName} Emoji Map` : ""),
  );
  const [emojiSetId, setEmojiSetId] = useState(initialId || "");
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [error, setError] = useState<string>("");

  useEffect(() => {
    // Validate emojis
    if (selectedEmojis.length !== requiredCount) {
      setError(`Please select exactly ${requiredCount} emojis (${selectedEmojis.length} selected)`);
    } else {
      setError("");
    }
  }, [selectedEmojis, requiredCount]);

  const categories = [
    { id: "all", name: "All", actualCategory: "all" },
    { id: "smileys", name: "Smileys & Emotion", actualCategory: "Smileys & Emotion" },
    { id: "people", name: "People & Body", actualCategory: "People & Body" },
    { id: "animals", name: "Animals & Nature", actualCategory: "Animals & Nature" },
    { id: "food", name: "Food & Drink", actualCategory: "Food & Drink" },
    { id: "activities", name: "Activities", actualCategory: "Activities" },
    { id: "travel", name: "Travel & Places", actualCategory: "Travel & Places" },
    { id: "objects", name: "Objects", actualCategory: "Objects" },
    { id: "symbols", name: "Symbols", actualCategory: "Symbols" },
    { id: "flags", name: "Flags", actualCategory: "Flags" }
  ];

  const filteredEmojis: EmojiData[] = (emojiData as EmojiDataFile).emojis.filter(emoji => {
    // Search filter
    const matchesSearch = searchTerm === '' || 
      emoji.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emoji.keywords.some(keyword => keyword.toLowerCase().includes(searchTerm.toLowerCase()));
    
    // Category filter
    const selectedCategory = categories.find(cat => cat.id === currentCategory);
    const matchesCategory = currentCategory === 'all' || emoji.category === selectedCategory?.actualCategory;

    // Version compatibility filter
    const matchesVersion = compatVersion === 'all' ||
      parseFloat(emoji.emoji_version) <= parseFloat(compatVersion);

    return matchesSearch && matchesCategory && matchesVersion;
  });

  const toggleEmoji = (emoji: EmojiData) => {
    const isSelected = selectedEmojis.some(e => e.code === emoji.unicode_code_point);
    let newSelection: SelectedEmoji[];
    
    if (isSelected) {
      newSelection = selectedEmojis.filter(e => e.code !== emoji.unicode_code_point);
    } else {
      if (selectedEmojis.length >= requiredCount) {
        setError(`You can only select ${requiredCount} emojis`);
        return;
      }
      
      // For ZWJ sequences, ensure we store the complete sequence
      const emojiToAdd = {
        char: emoji.emoji_char,
        code: emoji.unicode_code_point
      };
      
      newSelection = [...selectedEmojis, emojiToAdd];
    }

    // Update error state based on selection count
    if (newSelection.length !== requiredCount) {
      setError(`Please select exactly ${requiredCount} emojis (${newSelection.length} selected)`);
    } else {
      setError('');
    }

    // Notify parent of the change
    if (onSelectionChange) {
      onSelectionChange(newSelection);
    }
  };

  const randomizeEmojis = () => {
    const shuffled = [...filteredEmojis]
      .sort(() => Math.random() - 0.5)
      .slice(0, requiredCount)
      .map(emoji => ({
        char: emoji.emoji_char,
        code: emoji.unicode_code_point
      }));
    setError("");
    
    // Notify parent of the change
    if (onSelectionChange) {
      onSelectionChange(shuffled);
    }
  };

  const handleSave = () => {
    if (selectedEmojis.length !== requiredCount) {
      setError(`Please select exactly ${requiredCount} emojis`);
      return;
    }
    onSave(selectedEmojis, emojiSetName, emojiSetId);
  };

  return (
    <div className="h-full flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            type="text"
            placeholder="Search emojis..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        <Select value={compatVersion} onValueChange={setCompatVersion}>
          <SelectTrigger className="w-[175px] shrink-0">
            <SelectValue placeholder="Compatibility" />
          </SelectTrigger>
          <SelectContent>
            {COMPAT_OPTIONS.map(option => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={currentCategory} onValueChange={setCurrentCategory}>
          <SelectTrigger className="w-[130px] shrink-0">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            {categories.map(category => (
              <SelectItem key={category.id} value={category.id}>
                {category.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                onClick={randomizeEmojis}
                className="shrink-0"
              >
                <Shuffle className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Randomize selection</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <div className="flex items-center justify-between">
        {error ? (
          <p className="text-xs text-red-500">{error}</p>
        ) : (
          <span />
        )}
        <span className="flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap">
          {filteredEmojis.length.toLocaleString()} emojis
          {compatVersion === "15.1" && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <AlertTriangle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Some emojis may not display on older devices</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {compatVersion === "all" && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-500 cursor-help" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Includes emojis that may render as black bars on many devices</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </span>
      </div>

      <div className="flex-1 flex flex-col min-h-[300px]">
        <Card className="flex-1 overflow-hidden relative">
          <div className="h-full p-4 relative">
            <ScrollArea className="h-full">
              <TooltipProvider delayDuration={300}>
                <div className="grid grid-cols-12 gap-2 pr-4">
                  {filteredEmojis.map((emoji) => {
                    const isSelected = selectedEmojis.some(
                      (e) => e.code === emoji.unicode_code_point
                    );
                    return (
                      <Tooltip key={emoji.unicode_code_point}>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className={`h-8 w-8 p-0 text-xl hover:bg-gray-100 ${
                              isSelected ? "bg-purple-100 hover:bg-purple-200" : ""
                            }`}
                            onClick={() => toggleEmoji(emoji)}
                          >
                            {emoji.emoji_char}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <div className="text-center">
                            <p className="font-medium">{emoji.name}</p>
                            <p className="text-xs text-gray-400 mt-1">{emoji.unicode_code_point}</p>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    );
                  })}
                </div>
              </TooltipProvider>
            </ScrollArea>
          </div>
        </Card>
      </div>
    </div>
  );
}
